<template>
  <CircleSpinner v-if="isInitialLoading" />
  <RouterView
    v-else-if="categoryAllowed"
    :products="products"
    :page-info="pageInfo"
    :products-loading="loading"
    :product-category="productCategory"
    :kiosk-id="kioskGlobalId"
    :event="event"
    :current-page="currentPage"
    :per-page="perPage"
    @update:current-page="handlePageChange"
    @update:per-page="handlePerPageChange"
  />
</template>

<script setup lang="ts">
import { CircleSpinner } from '@ramp106/omrjs-core-ui'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useFestivalShopProductsQuery, type EventBySlugQuery, type ProductCategory } from '@/gql/myomr'
import { toGlobalId, toPlainId } from '@/helpers/globalIdHelper'
import { showNotification } from '@/helpers/notificationHelper'
import { globalizeUrlifiedId } from '@/helpers/urlifyGlobalId'
import { useFestivalShopStore } from '@/stores/festivalShop'

const props = defineProps<{
  event: EventBySlugQuery['eventBySlug']
  kioskId: string
  productCategoryId: string
  productCategories: ProductCategory[]
}>()

const router = useRouter()

const kioskGlobalId = computed(() => globalizeUrlifiedId(props.kioskId))
const productCategoryGlobalId = computed(() => toGlobalId('ProductCategory', props.productCategoryId))
type PerPage = number | 'all'

const defaultPerPage = 12
const currentPage = ref(1)
const perPage = ref<PerPage>(defaultPerPage)
const totalCount = ref(0)

const { result, loading } = useFestivalShopProductsQuery(
  () => ({
    rootEvent: { slug: props.event.rootEvent.slug },
    kioskId: kioskGlobalId.value,
    productCategoryId: productCategoryGlobalId.value,
    limit: perPage.value === 'all' ? totalCount.value || defaultPerPage : perPage.value,
    offset: perPage.value === 'all' ? 0 : (currentPage.value - 1) * perPage.value,
  }),
  { fetchPolicy: 'cache-and-network' },
)
const festivalShopStore = useFestivalShopStore()
const products = computed(() => result.value?.festivalShopProducts?.nodes ?? [])
const pageInfo = computed(() => result.value?.festivalShopProducts?.pageInfo)
const isInitialLoading = computed(() => loading.value && products.value.length === 0)
const productCategory = computed(() => {
  return props.productCategories
    .filter((pc) => {
      return toPlainId(pc.id)?.toString() == props.productCategoryId
    })
    .at(0)
})

const categoryAllowed = computed(() => !!productCategory.value)

watch(
  () => categoryAllowed.value,
  (newValue) => {
    if (!newValue) {
      router.push({ name: 'shopBookingOverview' })
      showNotification('Category is not found.', 'error')
    }
  },
  { immediate: true },
)

watch(
  () => props.productCategoryId,
  () => {
    currentPage.value = 1
  },
)

watch(
  result,
  (newValue) => {
    totalCount.value = newValue?.festivalShopProducts?.pageInfo?.totalCount ?? 0
  },
  { immediate: true },
)

watch(perPage, () => {
  currentPage.value = 1
})

watch(pageInfo, (info) => {
  if (!info?.pagesCount) return
  if (currentPage.value > info.pagesCount) {
    currentPage.value = info.pagesCount
  }
})

watch(
  productCategory,
  (category) => {
    festivalShopStore.setCurrentProductCategory(category ?? null)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  festivalShopStore.setCurrentProductCategory(null)
})

function handlePageChange(page: number) {
  currentPage.value = page
}

function handlePerPageChange(value: PerPage) {
  perPage.value = value
}
</script>
